import { Component, Input, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isEmpty, isEqual, isObject, merge } from 'lodash';
import { deepClone, ModalService, ButtonV2Module, InputV2Module } from '@schenck/rotec-components';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { ReportTemplate } from 'src/dto/report-template';
import { ReportTemplateService } from 'src/services';
import { ReportTemplateCacheService } from 'src/services/report-template-cache.service';
import { TemplateToolboxSettings } from 'src/utils/interfaces/export-settings.interface';
import { CancelEditModalComponent } from './modal/cancel-edit-modal.component';
import { TemplateFactory } from './template-factory';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { TemplateCanvasComponent } from './template-canvas/template-canvas.component';
import { MatCard } from '@angular/material/card';
import { TemplateToolboxComponent } from './template-toolbox/template-toolbox.component';

@Component({
  selector: 'rc-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.less'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ButtonV2Module,
    TranslatePipe,
    TranslateDirective,
    CdkDropListGroup,
    MatTabGroup,
    MatTab,
    TemplateCanvasComponent,
    MatCard,
    InputV2Module,
    TemplateToolboxComponent,
    AsyncPipe,
  ],
})
export class TemplateEditorComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly reportTemplateService = inject(ReportTemplateService);
  private readonly modalService = inject(ModalService);
  private readonly reportTemplateCacheService = inject(ReportTemplateCacheService);

  public template$: Observable<Partial<ReportTemplate>>;
  public toolboxSettings: TemplateToolboxSettings;
  public saveButtonDisabled$ = new BehaviorSubject(false);

  private cancel$: Subscription;
  private defaultTemplate: Partial<ReportTemplate>;
  private blankTemplate: ReportTemplate;
  private isTemplateDirty: boolean;
  private template: ReportTemplate;
  private originalTemplate: ReportTemplate;

  @Input() id: string;

  indexOfSelectedPage = 0;
  toolboxContainer = document;

  constructor() {
    this.defaultTemplate = TemplateFactory.createDefaultReportTemplate();
    this.blankTemplate = TemplateFactory.createBlankReportTemplate();
  }

  ngOnDestroy(): void {
    if (this.cancel$) {
      this.cancel$.unsubscribe();
    }
  }

  ngOnInit() {
    this.template$ = this.route.params.pipe(
      switchMap((params) => {
        if (isEmpty(params['id'])) {
          return of(this.blankTemplate);
        } else if (params['id'] === 'default') {
          //Removed all un-saved data in template only called while creating preffilled template
          this.reportTemplateCacheService.remove();
          return of(this.defaultTemplate);
        } else {
          return this.reportTemplateService.get(params['id']);
        }
      }),
      map((template) => {
        console.log('I am here .. : ', template);
        // NOTE: Fallback in case we have some invalid data in the backend (should never happen, though)
        if (!isObject(template.config)) {
          template.config = this.blankTemplate.config;
        }
        if (isEmpty(template.pages)) {
          template.pages = this.blankTemplate.pages;
        }
        if (isEmpty(template.userDefinedFields)) {
          template.userDefinedFields = this.blankTemplate.userDefinedFields;
        }

        const storedTemplate = this.reportTemplateCacheService.load();
        // Only do this if it is the same template
        if (storedTemplate && template.id === storedTemplate.id) {
          template = merge({}, template, storedTemplate);
        }

        return template as ReportTemplate;
      }),
      tap((template) => {
        this.template = template;
        this.originalTemplate = deepClone(template);
      }),
      finalize(() => {
        if (this.isTemplateDirty) {
          this.reportTemplateCacheService.save(this.template);
        }
      })
    );
  }

  saveReportTemplate(template: ReportTemplate): void {
    this.saveButtonDisabled$.next(true);
    this.reportTemplateService
      .createOrUpdate(template)
      .pipe(finalize(() => this.saveButtonDisabled$.next(false)))
      .subscribe(() => {
        this.isTemplateDirty = false;
        this.reportTemplateCacheService.remove();
        this.router.navigate(['/edit']);
      });
  }

  cancelReportTemplate(): void {
    this.cancel$ = this.modalService.open(CancelEditModalComponent).events$.subscribe(() => {
      this.isTemplateDirty = false;
      this.reportTemplateCacheService.remove();
      this.router.navigate(['/edit']);
    });
  }

  exportReportPreview(template: ReportTemplate, settings: TemplateToolboxSettings): void {
    if (!template) {
      return;
    }

    this.reportTemplateService.exportPreviewPdf(template, settings).subscribe((pdfBlob) => {
      // NOTE: To download instead of opening a new tab, activate the following
      // const date = new Date();
      // const filenamePrefix = template.name ? template.name : 'rotor_data_center_report';
      // const filenameSuffix = date
      //   .toISOString()
      //   .replace('T', '_')
      //   .replace(/\..+/, '');
      // const filename = `${filenamePrefix}-preview-${filenameSuffix}`;
      // downloadFile(pdfBlob, filename, 'pdf', 'application/pdf');

      // Open the preview PDF in a new tab (keep in mind that a popup blocker might prevent the new tab from being opened)
      const url = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    });
  }

  canSave(template: ReportTemplate): boolean {
    const validateDecimals = (value: number): boolean => value >= 0 && value <= 15;
    const validTemplateConfig = validateDecimals(template.config.numberOfDecimalPlaces);

    return isObject(template) && !isEmpty(template.name) && validTemplateConfig;
  }

  canExportReportPreview(template: ReportTemplate): boolean {
    // NOTE: The logic is currently identical to that of #canSave(), so delegate to that method for now.
    return this.canSave(template);
  }

  onTemplateChanged(): void {
    this.isTemplateDirty = !isEqual(this.template, this.originalTemplate);
  }

  public settingsChanged(settings: TemplateToolboxSettings): void {
    this.toolboxSettings = settings;
  }
}
